import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  alpha
} from '@mui/material';
import {
  CloudUpload,
  CloudDone,
  CloudOff,
  Folder,
  Add,
  CheckCircle
} from '@mui/icons-material';
import { 
  getDriveStatus, 
  getDriveAuthUrl, 
  listDriveFolders, 
  createDriveFolder,
  createScrapingFolder,
  disconnectDrive
} from '../api';

const UtilityBar = ({ url, onFolderSelected, selectedFolder = null, onConnectionChange, workflowStarted = false, driveConnectionDecision = null }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [folders, setFolders] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    try {
      const status = await getDriveStatus();
      const wasAuthenticated = isAuthenticated;
      setIsAuthenticated(status.authenticated);
      
      // Notify parent of connection change
      if (onConnectionChange && wasAuthenticated !== status.authenticated) {
        onConnectionChange(status.authenticated);
      }
      
      if (status.authenticated) {
        await loadFolders();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      const result = await listDriveFolders();
      setFolders(result.folders || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAuthenticate = async () => {
    try {
      setError('');
      const result = await getDriveAuthUrl();
      
      // Redirect to Google OAuth
      window.location.href = result.auth_url;
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectDrive();
      setIsAuthenticated(false);
      setFolders([]);
      setError('');
      handleCloseMenu();
      
      // Notify parent of connection change
      if (onConnectionChange) {
        onConnectionChange(false);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    setCreatingFolder(true);
    try {
      const result = await createDriveFolder(newFolderName.trim());
      setFolders([...folders, result.folder]);
      setNewFolderName('');
      setShowCreateDialog(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleCreateScrapingFolder = async () => {
    if (!url) {
      setError('URL is required to create scraping folder');
      return;
    }

    setCreatingFolder(true);
    try {
      const result = await createScrapingFolder(url);
      setFolders([...folders, result.folder]);
      onFolderSelected(result.folder);
      handleCloseMenu();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleSelectFolder = (folder) => {
    onFolderSelected(folder);
    handleCloseMenu();
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const getDriveStatusIndicator = () => {
    if (loading) {
      return (
        <Chip
          icon={<CircularProgress size={16} />}
          label="Checking..."
          size="small"
          variant="outlined"
          sx={{
            borderRadius: 2,
            backgroundColor: alpha('#f5f5f5', 0.8),
            borderColor: alpha('#e0e0e0', 0.5),
            '& .MuiChip-label': {
              fontWeight: 500,
              fontSize: '0.875rem'
            }
          }}
        />
      );
    }

    if (isAuthenticated) {
      return (
        <Tooltip title="Google Drive Connected">
          <Chip
            icon={<CloudDone />}
            label="Drive Connected"
            size="small"
            variant="outlined"
            sx={{
              borderRadius: 2,
              backgroundColor: alpha('#e8f5e8', 0.8),
              borderColor: alpha('#4caf50', 0.3),
              color: '#2e7d32',
              '& .MuiChip-label': {
                fontWeight: 500,
                fontSize: '0.875rem'
              }
            }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title="Google Drive Not Connected">
        <Chip
          icon={<CloudOff />}
          label="Drive Offline"
          size="small"
          variant="outlined"
          sx={{
            borderRadius: 2,
            backgroundColor: alpha('#f5f5f5', 0.8),
            borderColor: alpha('#e0e0e0', 0.5),
            color: 'text.secondary',
            '& .MuiChip-label': {
              fontWeight: 500,
              fontSize: '0.875rem'
            }
          }}
        />
      </Tooltip>
    );
  };

  const getDriveButton = () => {
    if (loading) {
      return (
        <Button 
          disabled 
          startIcon={<CircularProgress size={16} />}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 1
          }}
        >
          Loading...
        </Button>
      );
    }

    if (isAuthenticated) {
      return (
        <Button
          variant="contained"
          startIcon={<CloudDone />}
          onClick={handleMenuClick}
          size="small"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 1,
            backgroundColor: '#4caf50',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#45a049',
              boxShadow: alpha('#4caf50', 0.2) + ' 0px 4px 12px',
            }
          }}
        >
          Drive Connected
        </Button>
      );
    }

    const isDisabled = workflowStarted || driveConnectionDecision === 'skip';
    
    return (
      <Button
        variant="contained"
        startIcon={<CloudUpload />}
        onClick={handleAuthenticate}
        disabled={isDisabled}
        size="small"
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 500,
          px: 2,
          py: 1,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: alpha('#1976d2', 0.2) + ' 0px 4px 12px',
          },
          ...(isDisabled && {
            opacity: 0.6,
            '&:hover': {
              boxShadow: 'none',
            }
          })
        }}
      >
        {isDisabled ? 'Drive Connection Disabled' : 'Connect Drive'}
      </Button>
    );
  };

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError('')}
          sx={{ mb: 1 }}
        >
          {error}
        </Alert>
      )}

      {/* Utility Bar */}
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          backgroundColor: 'transparent',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: alpha('#e0e0e0', 0.3)
        }}
      >
        <Toolbar variant="dense" sx={{ py: 1 }}>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '1.25rem'
            }}
          >
            Web Content Scraper
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getDriveStatusIndicator()}
            {getDriveButton()}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drive Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedFolder && (
          <MenuItem disabled>
            <ListItemIcon>
              <CheckCircle color="success" />
            </ListItemIcon>
            <ListItemText 
              primary={`Selected: ${selectedFolder.name}`}
              secondary="Current folder for scraped content"
            />
          </MenuItem>
        )}

        <MenuItem onClick={() => setShowCreateDialog(true)}>
          <ListItemIcon>
            <Add />
          </ListItemIcon>
          <ListItemText primary="Create New Folder" />
        </MenuItem>

        {url && (
          <MenuItem onClick={handleCreateScrapingFolder} disabled={creatingFolder}>
            <ListItemIcon>
              <Folder />
            </ListItemIcon>
            <ListItemText 
              primary={creatingFolder ? "Creating..." : "Auto-Create Scraping Folder"} 
            />
          </MenuItem>
        )}

        {folders.length > 0 && (
          <MenuItem disabled>
            <ListItemText primary="Select Existing Folder:" sx={{ fontWeight: 'bold' }} />
          </MenuItem>
        )}
        
        {folders.slice(0, 5).map((folder) => (
          <MenuItem 
            key={folder.id}
            onClick={() => handleSelectFolder(folder)}
            selected={selectedFolder?.id === folder.id}
          >
            <ListItemIcon>
              <Folder color={selectedFolder?.id === folder.id ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText 
              primary={folder.name}
              secondary={new Date(folder.createdTime).toLocaleDateString()}
            />
          </MenuItem>
        ))}

        {folders.length > 5 && (
          <MenuItem disabled>
            <ListItemText primary={`... and ${folders.length - 5} more folders`} />
          </MenuItem>
        )}

        <MenuItem onClick={handleDisconnect} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <CloudOff color="error" />
          </ListItemIcon>
          <ListItemText primary="Disconnect Drive" />
        </MenuItem>
      </Menu>

      {/* Create Folder Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleCreateFolder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim() || creatingFolder}
          >
            {creatingFolder ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UtilityBar;

